/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2025 (original work) Open Assessment Technologies SA ;
 */
import { get, derived, writable } from 'svelte/store';
import reflowStore from '@/stores/reflow.store.js';
import * as localStorageHelper from '@/helpers/localStorageHelper.js';

/** Navigation menu drawer widths are arbitrary values, and can be adjusted
 * based on design requirements, passing props to the NavigationMenu component. */

export const DRAWER_EXPANDED_WIDTH_PX = 240;
export const DRAWER_COLLAPSED_WIDTH_PX = 64;

export const DEFAULT_EXPANDED_STATE = false;
export const DEFAULT_PUSH_MODE_STATE = true;
export const LOCALSTORAGE_KEY = 'navigationMenu-expanded';

export function createStore(defaults = {}) {
    let defaultExpanded = getExpandedSavedValue();
    const navMenuStore = writable({ pushMode: DEFAULT_PUSH_MODE_STATE, expanded: defaultExpanded, ...defaults });
    const { set, update } = navMenuStore; // Only the properties in the navigation menu store are writable

    const store = derived([navMenuStore, reflowStore], ([$navMenuStore, $reflowStore]) => {
        // We use the reflowStore widthThreshold to determine the pushMode of the navigation menu.
        // This allows the navigation menu adapting to different screen sizes.
        // The pushMode is determined by the page width and the nav menu drawer width.
        // If the page width is less than the narrowThreshold, the pushMode should be false
        // If the page width is greater than the wideThreshold, the pushMode should be true
        let navMenuOffset = $navMenuStore.expanded ? DRAWER_EXPANDED_WIDTH_PX : DRAWER_COLLAPSED_WIDTH_PX;
        const narrowThreshold = $reflowStore.widthThreshold + navMenuOffset;
        const wideThreshold = $reflowStore.widthThreshold + DRAWER_EXPANDED_WIDTH_PX; // Always use expanded width for testing to expand

        if ($reflowStore.pageWidth < narrowThreshold) {
            // Window is too narrow for push mode
            if ($navMenuStore.pushMode) {
                $navMenuStore.pushMode = false;
                $navMenuStore.expanded = false; // Collapse the menu when switching to overlay mode
            }
        } else if ($reflowStore.pageWidth > wideThreshold) {
            // Window is wide enough for push mode
            if (!$navMenuStore.pushMode) {
                $navMenuStore.pushMode = true;
                $navMenuStore.expanded = getExpandedSavedValue(); // Retrieve expanded state from LocalStorage
            }
        }

        return {
            ...$navMenuStore
        };
    });
    const { subscribe } = store;

    return {
        subscribe,
        set,
        update,
        get() {
            return get(store);
        },
        setExpanded(expanded) {
            // We need this to ensure that the expanded state is saved in the localStorage
            if (get(store).pushMode) {
                // But only save if it's in push mode (opening the nav menu
                // in overlay mode is not registered as a user preference)
                setExpandedSavedValue(expanded); // Save the expanded state to LocalStorage
            }
            update(state => {
                state.expanded = expanded;
                return state;
            });
        },
        reset() {
            // Reset the navigation menu to default values
            // Clear localStorage to ensure fresh state
            localStorageHelper.removeItem(LOCALSTORAGE_KEY);
            // Reset to default state
            // Note: The derived store logic might override these values based on reflow store state
            set({
                pushMode: DEFAULT_PUSH_MODE_STATE,
                expanded: DEFAULT_EXPANDED_STATE
            });
        }
    };
}

export default createStore();

/**
 * Retrieves the expanded state from the localStorage.
 * @returns {boolean} The expanded state from the localStorage, or undefined if not set
 */
export function getExpandedSavedValue() {
    const storedValue = localStorageHelper.getItem(LOCALSTORAGE_KEY);
    return storedValue?.expanded ?? DEFAULT_EXPANDED_STATE;
}
/**
 * Saves the expanded state to the localStorage.
 * @param {boolean} value - The expanded state to save
 */
export function setExpandedSavedValue(value) {
    localStorageHelper.setItem(LOCALSTORAGE_KEY, { expanded: value });
}
